import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { queryKeys } from "@/lib/queryKeys";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { SubtaskProgress } from "./SubtaskProgress";
import type { TaskRow } from "@shared/types";

/**
 * SubtaskList — designs/TaskDetail.html `.blk` + `.subtask` row.
 *
 * Each row is a clickable checkbox + title + assignee avatar; a
 * `.progress` bar at the top summarises completion. Adding a subtask
 * uses an inline form below the list.
 */

export function SubtaskList({
  taskId,
  projectId,
}: {
  taskId: string;
  projectId: string;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.subtasksOf(taskId),
    queryFn: () => api.listSubtasks(taskId),
  });
  const add = useMutation({
    mutationFn: (t: string) => api.createSubtask(projectId, taskId, t),
    onSuccess: () => {
      invalidateForTask(qc, { projectId, taskId });
      setTitle("");
      setAdding(false);
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.toggleTaskStatus(id, done ? "done" : "todo"),
    onSuccess: () => invalidateForTask(qc, { projectId, taskId }),
  });

  const subtasks: TaskRow[] = data?.subtasks ?? [];
  const done = subtasks.filter((s) => s.status === "done").length;
  const total = subtasks.length;

  return (
    <section className="blk" aria-label="Subtasks">
      <h3>
        Subtasks
        <span className="cnt">
          {done}/{total}
        </span>
        <button
          type="button"
          className="h-act"
          onClick={() => setAdding((a) => !a)}
        >
          + Add
        </button>
      </h3>

      {total > 0 && <SubtaskProgress done={done} total={total} />}

      {isLoading && <p className="t-sm t-muted">Loading subtasks…</p>}
      {isError && <p className="t-sm t-muted">Couldn't load subtasks.</p>}
      {!isLoading && !isError && total === 0 && !adding && (
        <p className="t-sm t-muted">No subtasks yet.</p>
      )}

      <div>
        {subtasks.map((s) => {
          const isDone = s.status === "done";
          const ownerId = s.assigneeIds?.[0] ?? s.assigneeId ?? null;
          return (
            <div key={s.id} className={`subtask${isDone ? " done" : ""}`}>
              <button
                type="button"
                className="cb"
                aria-label={
                  isDone ? "Mark subtask incomplete" : "Mark subtask complete"
                }
                aria-pressed={isDone}
                onClick={() => toggle.mutate({ id: s.id, done: !isDone })}
              >
                {isDone && <Icon name="check" size={13} />}
              </button>
              <span className="st-txt">{s.title}</span>
              {ownerId && <Avatar id={ownerId} name={ownerId} size="xs" />}
            </div>
          );
        })}
      </div>

      {adding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const v = title.trim();
            if (v) add.mutate(v);
          }}
          style={{ display: "flex", gap: 8, marginTop: 10 }}
        >
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Subtask title…"
            autoFocus
            style={{ flex: 1 }}
          />
          <button type="submit" disabled={add.isPending || !title.trim()}>
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setTitle("");
              setAdding(false);
            }}
          >
            Cancel
          </button>
        </form>
      )}
    </section>
  );
}
